import type { BroadcastSubject, BroadcastSubjectType } from '@/api/chat/v2-types';
import { PagePaths } from '@/routes/page-paths';

import type {
  ContextItem,
  ConversationEntry,
  ResolvedSendAsIdentity,
  StateFilter,
  ThreadMessageView,
  ThreadOverride,
  ThreadOverrides,
  ThreadSummary,
} from '../types';

const SUBJECT_ROOT_PATHS: Record<BroadcastSubjectType, string> = {
  Fund: PagePaths.allFunds,
  Company: PagePaths.deals,
  Event: PagePaths.events,
};

export const BROADCAST_SUBJECT_LINK_LABELS: Record<BroadcastSubjectType, string> = {
  Fund: 'View fund',
  Company: 'View company',
  Event: 'View event',
};

export const broadcastSubjectPath = (
  subject: BroadcastSubject | null | undefined,
): string | null => (subject ? `${SUBJECT_ROOT_PATHS[subject.type]}/${subject.id}` : null);

export const matchesStateFilter = (thread: ThreadSummary, filter: StateFilter | null): boolean => {
  if (filter === 'muted') return thread.isMuted;
  if (thread.isMuted) return false;

  switch (filter) {
    case 'needsAction':
      return thread.unreadCount > 0 || Boolean(thread.awaitingMyReply) || thread.isWaiting;
    case 'unread':
      return thread.unreadCount > 0;
    case 'awaitingReply':
      return Boolean(thread.awaitingMyReply);
    case 'waiting':
      return thread.isWaiting;
    default:
      return true;
  }
};

export const isThreadVisible = (thread: ThreadSummary): boolean =>
  !thread.isResolved && !thread.isArchived;

export const applyThreadOverrides = (
  contexts: ContextItem[],
  overrides: ThreadOverrides,
): ContextItem[] =>
  contexts.map((context) => ({
    ...context,
    threads: context.threads.map((thread) =>
      overrides[thread.id] ? { ...thread, ...overrides[thread.id] } : thread,
    ),
  }));

export const mergeConversation = (
  threads: ThreadSummary[],
  messagesByThread: Record<string, ThreadMessageView[]>,
): ConversationEntry[] => {
  const entries: ConversationEntry[] = [];

  threads.forEach((thread) => {
    const messages = messagesByThread[thread.id] ?? [];

    messages.forEach((message) => {
      if (message.isSystem) {
        entries.push({
          kind: 'system',
          id: message.id,
          threadId: thread.id,
          threadName: thread.name,
          text: message.body ?? '',
          sentAt: message.sentAt,
        });
        return;
      }

      entries.push({
        kind: 'message',
        id: message.id,
        threadId: thread.id,
        threadName: thread.name,
        topics: thread.topics,
        sender: message.sender,
        originalSenderId: message.originalSenderId,
        body: message.body,
        sentAt: message.sentAt,
        isOwn: message.isOwn,
        isUnread: message.isUnread ?? false,
        replyTo: message.replyTo,
        forwardedFrom: message.forwardedFrom,
        attachmentCount: message.attachmentCount,
        attachments: message.attachments,
        isBroadcast: thread.isBroadcast,
        link: message.link,
      });
    });
  });

  return entries.sort((a, b) => new Date(a.sentAt).getTime() - new Date(b.sentAt).getTime());
};

export const buildOutgoingMessage = (
  body: string,
  identity: ResolvedSendAsIdentity,
  now: Date,
  seq: number,
): ThreadMessageView => ({
  id: `tm-out-${now.getTime()}-${seq}`,
  sender: {
    id: identity.participantId,
    name: identity.label,
    kind: 'contact',
  },
  body,
  sentAt: now.toISOString(),
  isOwn: true,
});

export const threadFromCreated = (
  created: {
    id: string;
    name: string;
    isBroadcast: boolean;
    lastMessagePreview: string | null;
    createdAt: string;
  },
  ownIdentity: ThreadSummary['ownIdentity'],
): ThreadSummary => ({
  id: created.id,
  name: created.name,
  topics: [],
  preview: created.lastMessagePreview ?? '',
  lastActivityAt: created.createdAt,
  unreadCount: 0,
  hasMention: false,
  isWaiting: false,
  isMuted: false,
  isPrivate: false,
  isBroadcast: created.isBroadcast,
  isOwnBroadcast: created.isBroadcast,
  ownIdentity,
});

export const overrideAfterSending = (body: string, sentAt: string): ThreadOverride => ({
  preview: body,
  lastActivityAt: sentAt,
  unreadCount: 0,
  isWaiting: false,
});
